import { createContext, useContext } from 'react';

import type { I18nFormats } from '@shared/i18n/currency.js';
import type { Bind, Locale } from '@shared/i18n/types.js';
import type { WebCatalog } from '@shared/i18n/web-catalog-type.js';

/**
 * Контекст привязанного каталога локали.
 *
 * ⚠️ ИМЕНОВАНИЕ ЗДЕСЬ НЕ КОСМЕТИКА. Линтер локалей (scripts/i18n/lint.ts) запрещает:
 *   - правило `shadow-t`: переменную или параметр с именем ровно `t` в файле,
 *     который где-либо вызывает `t(` или `tx(`;
 *   - правило `lazy-t`: вызов идентификатора `t`/`tx` на верхнем уровне модуля.
 * Оба правила работают и в web/src, исключений в allowlist нет ни одного.
 *
 * Хук с именем `useT()` прямо провоцирует писать `const t = useT();` в каждом
 * компоненте — ровно тот shadow, который ловит правило. Поэтому идентификатор `t`
 * на клиенте не вводится ВООБЩЕ: оба правила становятся структурно недостижимыми,
 * а не «соблюдаются дисциплиной». Каталог читается как `c.common.pay`,
 * сообщения с параметрами — как `c.common.days({ count: 5 })`.
 */
export interface I18nContextValue {
  readonly catalog: Bind<WebCatalog>;
  readonly locale: Locale;
  readonly htmlLang: string;
  readonly formats: I18nFormats;
  /**
   * Адрес карточки приложения в App Store. Пустая строка = приложения у этого деплоя нет.
   *
   * ⚠️ Живёт в контексте локали не по смыслу, а по транспорту: манифест — единственное,
   * что приезжает с сервера до первого рендера на КАЖДОЙ странице. Заводить ради одной
   * строки второй запрос и второй провайдер дороже, чем это соседство.
   */
  readonly iosAppStoreUrl: string;
  /** Адрес карточки в Google Play. Пусто = приложения нет. Транспорт тот же. */
  readonly androidAppStoreUrl: string;
}

export const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function useI18nContext(): I18nContextValue {
  const value = useContext(I18nContext);
  if (value === undefined) {
    throw new Error('useCatalog()/useI18nFormats() called outside <CatalogProvider>');
  }
  return value;
}

/** Каталог локали, привязанный к языку деплоя. */
export function useCatalog(): Bind<WebCatalog> {
  return useI18nContext().catalog;
}

/** Параметры Intl (BCP-47 локаль и код валюты) — см. shared/i18n/currency.ts. */
export function useI18nFormats(): I18nFormats {
  return useI18nContext().formats;
}

/** Адрес карточки в App Store; пусто — приложения нет (components/layout/IosAppBanner.tsx). */
export function useIosAppStoreUrl(): string {
  return useI18nContext().iosAppStoreUrl;
}

/** Адрес карточки в Google Play; пусто — приложения нет (components/layout/AndroidAppBanner.tsx). */
export function useAndroidAppStoreUrl(): string {
  return useI18nContext().androidAppStoreUrl;
}
